import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getUnitsByProject, uploadExcelFile, getProjectById } from '../utils/api';
import './PropertyManagementPage.css';

const PropertyManagementPage = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const projectName = searchParams.get('name') || '';
  
  const [project, setProject] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // 'replace' treats the file as a full provider snapshot: units of this
  // project/provider missing from it are marked sold. 'append' upserts only.
  const [uploadMode, setUploadMode] = useState('replace');
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  const fileInputRef = useRef(null);

  // Fetch project details
  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await getProjectById(projectId);
      setProject(data);
    } catch (err) {
      console.error('Error fetching project:', err);
    }
  }, [projectId]);

  // Fetch units for the project
  const fetchUnits = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const data = await getUnitsByProject(parseInt(projectId));
      setUnits(data.units || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching units:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
    fetchUnits();
  }, [fetchProject, fetchUnits]);

  // Handle Excel file upload
  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      setError('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    setUploadProgress('Processing Excel file...');
    setError(null);

    try {
      const result = await uploadExcelFile(file, parseInt(projectId), uploadMode);
      const extractedCount = result.properties?.length || 0;
      const save = result.processing_summary?.database_save || {};
      const savedCount = save.new_units || 0;
      const updatedCount = save.updated_units || 0;
      const soldCount = save.marked_unavailable || 0;
      const downgraded = result.processing_summary?.reconciliation_skipped_reason;

      if (extractedCount > 0) {
        let msg = `Success! Extracted ${extractedCount} units. Saved: ${savedCount} new, ${updatedCount} updated`;
        if (soldCount > 0) msg += `, ${soldCount} marked sold`;
        setUploadProgress(msg + '.');
        if (downgraded) setError(downgraded);
      } else {
        setUploadProgress(`Warning: No properties extracted. Check the Excel format.`);
      }
      
      // Refresh units list
      await fetchUnits();
      
      setTimeout(() => {
        setUploadProgress(null);
      }, 5000);
    } catch (err) {
      setError('Failed to process Excel: ' + err.message);
      setUploadProgress(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Filter units based on search term
  const filteredUnits = units.filter(unit => {
    const searchLower = searchTerm.toLowerCase();
    return (
      unit.unit_type?.toLowerCase().includes(searchLower) ||
      unit.finishing_type?.toLowerCase().includes(searchLower) ||
      String(unit.total_price).includes(searchTerm) ||
      String(unit.area).includes(searchTerm) ||
      String(unit.no_of_rooms).includes(searchTerm)
    );
  });

  // Sort units
  const sortedUnits = [...filteredUnits].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    
    // Handle numeric values
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    // Handle strings
    aVal = String(aVal || '').toLowerCase();
    bVal = String(bVal || '').toLowerCase();
    
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Handle column sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('en-EG', {
      style: 'currency',
      currency: 'EGP',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Format area
  const formatArea = (value) => {
    if (!value && value !== 0) return '-';
    return `${value} m²`;
  };

  // Get sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="property-management-page">
      {/* Header */}
      <header className="pm-header">
        <div className="pm-header-left">
          <h1>📊 Property Units</h1>
          <div className="pm-project-info">
            <span className="pm-project-name">{project?.project_name || projectName}</span>
            {project?.location_description && (
              <span className="pm-project-location">
                📍 {project.location_description}
              </span>
            )}
          </div>
        </div>
        <div className="pm-header-right">
          <div className="pm-stats">
            <div className="pm-stat">
              <span className="pm-stat-value">{units.length}</span>
              <span className="pm-stat-label">Total Units</span>
            </div>
            <div className="pm-stat">
              <span className="pm-stat-value">{filteredUnits.length}</span>
              <span className="pm-stat-label">Showing</span>
            </div>
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <div className="pm-toolbar">
        <div className="pm-toolbar-left">
          <div className="pm-search-box">
            <span className="pm-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search units by type, price, area..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pm-search-input"
            />
            {searchTerm && (
              <button 
                className="pm-search-clear"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>
        </div>
        
        <div className="pm-toolbar-right">
          <label className="pm-upload-mode" title="Replace: the file is a full availability snapshot — units missing from it are marked sold. Append: add and update only.">
            <select
              value={uploadMode}
              onChange={(e) => setUploadMode(e.target.value)}
              disabled={uploading}
            >
              <option value="replace">Replace (full snapshot)</option>
              <option value="append">Append (add / update only)</option>
            </select>
          </label>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleExcelUpload}
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
          />
          <button 
            className="pm-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <span className="pm-spinner"></span>
                Processing...
              </>
            ) : (
              <>
                📤 Upload Excel
              </>
            )}
          </button>
        </div>
      </div>

      {/* Upload Progress */}
      {uploadProgress && (
        <div className="pm-upload-progress">
          <span className="pm-progress-icon">✅</span>
          {uploadProgress}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="pm-error">
          <span className="pm-error-icon">⚠️</span>
          {error}
          <button className="pm-error-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Content */}
      <div className="pm-content">
        {loading ? (
          <div className="pm-loading">
            <div className="pm-loading-spinner"></div>
            <p>Loading property units...</p>
          </div>
        ) : units.length === 0 ? (
          <div className="pm-empty">
            <div className="pm-empty-icon">📋</div>
            <h2>No Property Units</h2>
            <p>Upload an Excel file to add property units to this project.</p>
            <button 
              className="pm-upload-btn-large"
              onClick={() => fileInputRef.current?.click()}
            >
              📤 Upload Excel File
            </button>
          </div>
        ) : (
          <div className="pm-table-container">
            <table className="pm-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('id')}>
                    ID {getSortIndicator('id')}
                  </th>
                  <th onClick={() => handleSort('unit_type')}>
                    Unit Type {getSortIndicator('unit_type')}
                  </th>
                  <th onClick={() => handleSort('no_of_rooms')}>
                    Rooms {getSortIndicator('no_of_rooms')}
                  </th>
                  <th onClick={() => handleSort('area')}>
                    Area {getSortIndicator('area')}
                  </th>
                  <th onClick={() => handleSort('total_price')}>
                    Price {getSortIndicator('total_price')}
                  </th>
                  <th onClick={() => handleSort('finishing_type')}>
                    Finishing {getSortIndicator('finishing_type')}
                  </th>
                  <th onClick={() => handleSort('delivery_date')}>
                    Delivery {getSortIndicator('delivery_date')}
                  </th>
                  <th onClick={() => handleSort('confidence_score')}>
                    Confidence {getSortIndicator('confidence_score')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedUnits.map((unit) => (
                  <tr key={unit.id}>
                    <td className="pm-cell-id">{unit.id}</td>
                    <td className="pm-cell-type">
                      <span className="pm-type-badge">{unit.unit_type || '-'}</span>
                    </td>
                    <td className="pm-cell-rooms">
                      {unit.no_of_rooms === 0 ? 'Studio' : (unit.no_of_rooms || '-')}
                    </td>
                    <td className="pm-cell-area">{formatArea(unit.area)}</td>
                    <td className="pm-cell-price">{formatCurrency(unit.total_price)}</td>
                    <td className="pm-cell-finishing">{unit.finishing_type || '-'}</td>
                    <td className="pm-cell-delivery">{unit.delivery_date || '-'}</td>
                    <td className="pm-cell-confidence">
                      <div className="pm-confidence-bar">
                        <div 
                          className="pm-confidence-fill"
                          style={{ 
                            width: `${(unit.confidence_score || 0) * 100}%`,
                            backgroundColor: unit.confidence_score >= 0.8 ? '#10b981' : 
                                           unit.confidence_score >= 0.5 ? '#f59e0b' : '#ef4444'
                          }}
                        ></div>
                        <span className="pm-confidence-text">
                          {((unit.confidence_score || 0) * 100).toFixed(0)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="pm-footer">
        <div className="pm-footer-left">
          <span>Source: {units[0]?.source_file || 'N/A'}</span>
        </div>
        <div className="pm-footer-right">
          <button 
            className="pm-refresh-btn"
            onClick={fetchUnits}
            disabled={loading}
          >
            🔄 Refresh
          </button>
        </div>
      </footer>
    </div>
  );
};

export default PropertyManagementPage;
